import { Head, Link } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { formatCents } from "@/lib/fetch-json"

interface DealSummary {
  id: number
  name: string
  slug: string
  max_quantity: number | null
  redemptions_count: number
}

interface RedemptionRow {
  id: number
  account_id: number
  account_name: string
  owner_email: string | null
  status: string
  amount_cents: number
  completed_at: string | null
  created_at: string
}

export default function AdminLifetimeDealRedemptions({
  deal,
  redemptions,
}: {
  deal: DealSummary
  redemptions: RedemptionRow[]
}) {
  return (
    <>
      <Head title={`${deal.name} Redemptions`}>
        <meta name="description" content={`Redemptions of the ${deal.name} lifetime deal.`} />
        <meta property="og:title" content={`${deal.name} Redemptions`} />
        <meta property="og:description" content={`Redemptions of the ${deal.name} lifetime deal.`} />
      </Head>
      <AdminShell>
        <PageHeader
          title={`${deal.name} — Redemptions`}
          description={`${deal.redemptions_count}${deal.max_quantity != null ? ` of ${deal.max_quantity}` : ""} redeemed.`}
          actions={
            <Link href="/admin/lifetime_deals" className="text-sm text-ink-muted no-underline hover:text-ink-display">
              Back to deals
            </Link>
          }
        />

        {redemptions.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No redemptions yet.</p>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Account</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Owner</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Amount</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Completed</th>
                </tr>
              </thead>
              <tbody>
                {redemptions.map((redemption) => (
                  <tr key={redemption.id} className="border-b border-hairline last:border-0">
                    <td className="px-4 py-3">
                      <Link href={`/admin/accounts/${redemption.account_id}`}>{redemption.account_name}</Link>
                    </td>
                    <td className="px-4 py-3">{redemption.owner_email ?? "—"}</td>
                    <td className="px-4 py-3">
                      <RedemptionStatusBadge status={redemption.status} />
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {formatCents(redemption.amount_cents)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {redemption.completed_at ? formatDate(redemption.completed_at) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminShell>
    </>
  )
}

function RedemptionStatusBadge({ status }: { status: string }) {
  const tone =
    status === "completed" ? ("accent" as const) : status === "pending" ? ("signal" as const) : ("muted" as const)
  return <Badge tone={tone}>{status}</Badge>
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}
